import React, { useState } from 'react';
import {
  useSecrets,
  useCreateSecret,
  useUpdateSecret,
  useDeleteSecret,
  SecretMetadata,
} from '../helpers/useSecretsApi';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Badge } from './Badge';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Key, Plus, Trash2, Eye, EyeOff, Edit2, Check, X, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnly } from '../helpers/formatDateUTC';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import styles from './SessionSecretsPanel.module.css';

interface SessionSecretsPanelProps {
  sessionId: string;
  isOwner?: boolean;
}

export const SessionSecretsPanel: React.FC<SessionSecretsPanelProps> = ({
  sessionId,
  isOwner = false,
}) => {
  const { data, isLoading, error } = useSecrets(sessionId);
  const createMutation = useCreateSecret();
  const updateMutation = useUpdateSecret();
  const deleteMutation = useDeleteSecret();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSecret, setEditingSecret] = useState<SecretMetadata | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<SecretMetadata | null>(null);

  // Form state
  const [keyName, setKeyName] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [showValue, setShowValue] = useState(false);

  const resetForm = () => {
    setKeyName('');
    setValue('');
    setDescription('');
    setShowValue(false);
  };

  const handleCreate = async () => {
    if (!keyName.trim() || !value.trim()) {
      toast.error('Key name and value are required');
      return;
    }

    try {
      await createMutation.mutateAsync({
        sessionId,
        keyName: keyName.trim().toUpperCase(),
        value: value.trim(),
        description: description.trim() || undefined,
      });
      toast.success('Secret created');
      setShowAddDialog(false);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create secret');
    }
  };

  const handleUpdate = async () => {
    if (!editingSecret) return;

    try {
      await updateMutation.mutateAsync({
        secretId: editingSecret.id,
        value: value.trim() || undefined,
        description: description.trim(),
      });
      toast.success('Secret updated');
      setEditingSecret(null);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update secret');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteMutation.mutateAsync({
        secretId: deleteConfirm.id,
        sessionId,
      });
      toast.success('Secret deleted');
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete secret');
    }
  };

  const openEditDialog = (secret: SecretMetadata) => {
    setEditingSecret(secret);
    setValue('');
    setDescription(secret.description || '');
    setShowValue(false);
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            <Key size={18} />
            Environment Secrets
          </h3>
        </div>
        <div className={styles.list}>
          {[1, 2].map((i) => (
            <div key={i} className={styles.skeletonItem}>
              <Skeleton style={{ width: '100%', height: '60px' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <AlertCircle size={24} />
          <p>Failed to load secrets</p>
        </div>
      </div>
    );
  }

  const secrets = data?.secrets || [];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>
            <Key size={18} />
            Environment Secrets
          </h3>
          <Badge variant="secondary">{secrets.length}</Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddDialog(true)}
          className={styles.addButton}
        >
          <Plus size={14} />
          Add Secret
        </Button>
      </div>

      <div className={styles.infoBox}>
        <Info size={14} />
        <span>
          Secrets are encrypted and available as environment variables in your published sites.
        </span>
      </div>

      {secrets.length === 0 ? (
        <div className={styles.emptyState}>
          <Key size={32} className={styles.emptyIcon} />
          <p className={styles.emptyText}>No secrets configured</p>
          <p className={styles.emptySubtext}>
            Add environment variables like API keys that your published site can use.
          </p>
        </div>
      ) : (
        <div className={styles.list}>
          {secrets.map((secret) => (
            <div key={secret.id} className={styles.secretItem}>
              <div className={styles.secretInfo}>
                <div className={styles.secretKey}>
                  <code>{secret.keyName}</code>
                </div>
                {secret.description && (
                  <div className={styles.secretDesc}>{secret.description}</div>
                )}
                <div className={styles.secretMeta}>
                  Updated {secret.updatedAt ? formatDateOnly(secret.updatedAt) : 'recently'}
                </div>
              </div>
              <div className={styles.secretActions}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon-sm" onClick={() => openEditDialog(secret)}>
                      <Edit2 size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit secret</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setDeleteConfirm(secret)}
                      className={styles.deleteButton}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete secret</TooltipContent>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Secret Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Secret</DialogTitle>
            <DialogDescription>
              Create a new environment variable for your published sites.
            </DialogDescription>
          </DialogHeader>
          <div className={styles.formFields}>
            <div className={styles.formField}>
              <label>Key Name</label>
              <Input
                placeholder="e.g., API_KEY"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value.toUpperCase())}
                className={styles.keyInput}
              />
              <span className={styles.hint}>
                Must be uppercase with underscores (e.g., DATABASE_URL)
              </span>
            </div>
            <div className={styles.formField}>
              <label>Value</label>
              <div className={styles.valueInputWrapper}>
                <Input
                  type={showValue ? 'text' : 'password'}
                  placeholder="Secret value..."
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowValue(!showValue)}
                  className={styles.toggleVisibility}
                >
                  {showValue ? <EyeOff size={14} /> : <Eye size={14} />}
                </Button>
              </div>
            </div>
            <div className={styles.formField}>
              <label>Description (optional)</label>
              <Textarea
                placeholder="What is this secret used for?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? <Spinner size="sm" /> : 'Create Secret'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Secret Dialog */}
      <Dialog open={!!editingSecret} onOpenChange={() => setEditingSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Secret</DialogTitle>
            <DialogDescription>
              Update the value for <code>{editingSecret?.keyName}</code>
            </DialogDescription>
          </DialogHeader>
          <div className={styles.formFields}>
            <div className={styles.formField}>
              <label>New Value (leave empty to keep current)</label>
              <div className={styles.valueInputWrapper}>
                <Input
                  type={showValue ? 'text' : 'password'}
                  placeholder="Enter new value..."
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowValue(!showValue)}
                  className={styles.toggleVisibility}
                >
                  {showValue ? <EyeOff size={14} /> : <Eye size={14} />}
                </Button>
              </div>
            </div>
            <div className={styles.formField}>
              <label>Description</label>
              <Textarea
                placeholder="What is this secret used for?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingSecret(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? <Spinner size="sm" /> : 'Update Secret'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Secret</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <code>{deleteConfirm?.keyName}</code>? This action
              cannot be undone and may break your published sites.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
